import { useContext } from 'react';
import { NavigationContext } from '../NavigationContext';
import { useBillingNavigation } from './useBillingNavigation';
import { useIdentity } from './useIdentity';
import { useTenantConfig } from './useTenantConfig';

export const GLOBAL_NAV_ENV_BANNER_CONFIG_KEY =
  'ui.lighthouse.newUI.envBanner.enabled';

export const GLOBAL_NAV_ENV_BANNER_PERMISSION =
  'permission.EnableEnvironmentBannerConfig';

export const useEnvironmentBanner = (
  { enabled }: { enabled: boolean } = { enabled: true },
) => {
  const { suppressBillingEnvironmentBanner } = useContext(NavigationContext);
  const { data } = useBillingNavigation({ enabled });
  const shouldFetchGlobalNavEnvBannerConfig =
    enabled && suppressBillingEnvironmentBanner;
  const { data: configData, isLoading: isTenantConfigLoading } =
    useTenantConfig({
      enabled: shouldFetchGlobalNavEnvBannerConfig,
      configKeys: [GLOBAL_NAV_ENV_BANNER_CONFIG_KEY],
    });
  /* Global nav only: the puppet key gates tenants, the permission gates users,
   * and either one is enough. Legacy nav never consults them. Uses the raw
   * permission list rather than `hasPermission`, which allows everything on OneID. */
  const { data: identity, isLoading: isIdentityLoading } = useIdentity({
    enabled: shouldFetchGlobalNavEnvBannerConfig,
  });
  const isGlobalNavEnvBannerEnabled =
    configData?.[GLOBAL_NAV_ENV_BANNER_CONFIG_KEY] === 'true' ||
    !!identity?.permissions?.includes(GLOBAL_NAV_ENV_BANNER_PERMISSION);
  const isEnvBannerDisplayOn = data?.envBanner?.display === 'ON';

  return {
    showEnvironmentBanner:
      enabled &&
      isEnvBannerDisplayOn &&
      (!suppressBillingEnvironmentBanner || isGlobalNavEnvBannerEnabled),
    isLoading:
      enabled &&
      (!data ||
        (shouldFetchGlobalNavEnvBannerConfig &&
          (isTenantConfigLoading || isIdentityLoading))),
  };
};
